import React, { useState, useEffect } from 'react';
import axios from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Gift, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const EmailCapturePopup = ({ delay = 5000 }) => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if already dismissed or submitted
    const dismissed = localStorage.getItem('email_popup_dismissed');
    const alreadySignedUp = localStorage.getItem('email_popup_submitted');
    
    if (dismissed || alreadySignedUp) return;

    // Show popup after delay
    const timer = setTimeout(() => {
      setShow(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('email_popup_dismissed', Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/cda/email-signup', {
        email: email.trim(),
        name: name.trim(),
        source: 'popup',
      });
      setSubmitted(true);
      localStorage.setItem('email_popup_submitted', 'true');
      toast.success('Check your email for your free CDA checklist! 📋');
    } catch (err) {
      console.error('Email signup failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
          data-testid="popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              You're all set! 🎉
            </h2>
            <p className="text-slate-600 mb-6">
              Check your inbox for your free CDA checklist. We'll also send you monthly tips and resources to help you succeed.
            </p>
            <Button onClick={handleDismiss} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
              Continue to Care Comply
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0d7d6f] to-[#0a6358] p-6 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-7 h-7" />
              </div>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Free CDA Checklist
              </h2>
              <p className="text-white/90 text-sm">
                Get your step-by-step CDA credential checklist — free!
              </p>
            </div>

            {/* Form */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Your first name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-11"
                    data-testid="popup-name"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="popup-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#c4622d] hover:bg-[#a8501f] text-base"
                  data-testid="popup-submit"
                >
                  {submitting ? 'Sending...' : 'Send My Free Checklist'}
                </Button>
              </form>

              <p className="text-xs text-slate-400 text-center mt-4">
                We'll also send you monthly CDA tips. Unsubscribe anytime.
              </p>

              <button
                onClick={handleDismiss}
                className="text-sm text-slate-400 hover:text-slate-600 mx-auto block mt-3"
              >
                No thanks, I'll figure it out myself
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailCapturePopup;
